import type { CountryCode } from 'libphonenumber-js';
import React from 'react';
import { View } from 'react-native';

import { Stack } from '@/components/layout';
import { TextInput } from '@/components/ui';
import type { TextInputFieldProps } from '@/components/ui/text-input/types';
import { InputLabel } from '@/features/auth/components/input-label';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type AuthPhoneFieldProps = {
  label: string;
  required?: boolean;
  value: string;
  onChangeText: (text: string, countryCode?: CountryCode) => void;
  onBlur?: TextInputFieldProps['onBlur'];
  /** Default country used to format the number and pick the leading flag. */
  defaultCountry?: CountryCode;
  error?: string;
  placeholder?: string;
};

/**
 * Phone field used in the sign-up flow: a flag + vertical divider on the left of a
 * formatted phone number input. Mirrors {@link AuthTextField} chrome (48px tall,
 * 8px radius, slate border) so the form reads as one set of inputs.
 */
export function AuthPhoneField({
  label,
  required,
  value,
  onChangeText,
  onBlur,
  defaultCountry = 'US',
  error,
  placeholder,
}: AuthPhoneFieldProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const hasError = !!error;

  return (
    <Stack gap={scale(4)}>
      <InputLabel required={required}>{label}</InputLabel>
      <TextInput
        phoneNumber
        phoneDefaultCountry={defaultCountry}
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        status={hasError ? 'error' : 'default'}>
        <TextInput.Content style={[styles.content, hasError && styles.contentError]}>
          <TextInput.Flag styleWrapper={styles.flag} />
          <View style={styles.divider} />
          <TextInput.Field
            placeholder={placeholder}
            variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
            keyboardType="phone-pad"
            autoCorrect={false}
            placeholderTextColor={colors['5']}
            maxLength={24}
          />
        </TextInput.Content>
        {/*
          Keep the HelperText permanently mounted and only toggle its content/height
          (same fix as AuthTextField): a `{hasError ? <HelperText/> : null}` last child
          can fail to re-mount when the error clears then returns, leaving a red border
          with no message. Never unmounting it avoids that reconciliation gap.
        */}
        <TextInput.HelperText style={hasError ? undefined : styles.helperTextHidden}>
          {error ?? ''}
        </TextInput.HelperText>
      </TextInput>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  content: {
    height: scale(48),
    borderRadius: scale(8),
    borderColor: colors['3'],
    paddingHorizontal: scale(12),
  },
  contentError: {
    borderColor: colors.Error,
  },
  flag: {
    marginRight: scale(8),
    marginLeft: scale(8),
  },
  divider: {
    width: scale(1),
    height: scale(24),
    marginRight: scale(8),
    backgroundColor: colors['3'],
  },
  // Collapse the (always-mounted) helper text when there's no error, so it takes no space.
  helperTextHidden: {
    height: 0,
    marginTop: 0,
  },
}));

export default AuthPhoneField;
